import { tryCatch } from "@/utils/try-catch";
import { useTranslation } from "react-i18next";
import { useLocation, useHistory } from "react-router-dom";
import { useState, useEffect, ChangeEvent } from "react";
import { useForm, SubmitHandler, UseFormRegister } from "react-hook-form";
import { AuthFlowType, FLOW_CONFIGS, FlowConfig } from "@/config/RecoverPinFlowConfig";

import { useApiResponseHandler } from "@/hooks/useApiResponseHandler";

import { FormError } from "@/models/continente-credentials/common/FormError"

import { Button } from "@/components/ui/button";
import Input from "@/components/ui/Input";
import CredentialsForm from "@/components/Form/Form";
import CredentialsFooter from "@/components/footer/Footer";
import CredentialsHeader from "@/components/header/Header";
import NeedHelpButton from "@/components/need-help-button/need-help-button";
import Divider from "@/components/divider";
import FreshStartButton from "@/components/fresh-start-button/fresh-start-button";

import { PinRecoverRequest } from "@/models/shared/PinRecoverRequest";
import { pinRecoverService } from "@/services/shared/PinRecoverService";
import { genericService } from "@/services/GenericService";
import { FreshStartRequest, FreshStartResponse } from "@/models/shared/freshStart/FreshStartModels";


const ANCHOR_DISABLE_LOYALTYCARD_ASSOCIATION = "";

interface RecoverPinPageProps {
  flowConfig: FlowConfig;
}

type Form = PinRecoverRequest & FormError;

export default function RecoverPinPage({ flowConfig }: RecoverPinPageProps) {
  const history = useHistory();
  const location = useLocation<any>();
  const { t } = useTranslation();

  const { handleResponse } = useApiResponseHandler();

  const [loading, setLoading] = useState(false);
  const [cardValue, setCardValue] = useState<string>();
  const [labelCardVisible, setLabelCardVisible] = useState<boolean>(false);
  const [isAdminBlocked, setAdminBlocked] = useState(false);

  const userData = location.state?.properties || {};
  const nextStep = location.state?.nextStep || "";
  const email = userData.phoneNumber || "";
  const phoneNumber = userData.email || "";
  const phoneFreshStartEligible = userData.phoneFreshStartEligible === true;

  const formState = useForm<Form>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = formState;

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (
      e.key !== "Enter" &&
      (!e.key.match(/[0-9\s]/) ||
        (e.target as HTMLInputElement).value.length > 12)
    ) {
      e.preventDefault();
    }
  };

  const handleCardChange = ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>) => {
    clearErrors();

    let cardNumber = value.replace(/\s/g, "");

    setValue("cardNumber", cardNumber);
    setLabelCardVisible(cardNumber.length > 0 ? true : false);
    setCardValue(cardNumber);
  };

  useEffect(() => {
    const CardValidator = (register: UseFormRegister<Form>) =>
      register("cardNumber", {
        required: {
          value: true,
          message: t("common.required_field"),
        },
        pattern: {
          value: /^[0-9]{13}$/,
          message: t("card_association.invalid_loyaltycard"),
        },
      });
    CardValidator(register);
  }, [register, t]);

  const handleContactFormLinkClick = () => {
    history.push("/continente-credentials/faq" + ANCHOR_DISABLE_LOYALTYCARD_ASSOCIATION);
  };

  const onSubmit: SubmitHandler<PinRecoverRequest> = async (data) => {
    setLoading(true);
    const { data: response, error } = await tryCatch(
      pinRecoverService.pinRecover(
        {
          cardNumber: data.cardNumber,
          email: email,
          phoneNumber: phoneNumber,
        },
        nextStep
      )
    );

    handleResponse(response, {
      setError,
      setAdminBlocked,
    }, error);

    setLoading(false);
  };

  const freshStart = async () => {
    setLoading(true);

    const { data: response, error } = await tryCatch(genericService.post<FreshStartResponse, FreshStartRequest>(
      "api/phonenumber/login/fresh-start",
      {
        origin: flowConfig.freshStartOrigin
      }
    ));

    handleResponse(response, { setError }, error);
    setLoading(false);
  };

  return (
    <CredentialsForm
      id={flowConfig.form}
      autoComplete="on"
      form={formState}
      onSubmit={(e) => {
        clearErrors();
        handleSubmit(onSubmit)(e);
      }}
    >
      <CredentialsHeader
        title={t(flowConfig.labels.headerTitle)}
        subtitle={t(flowConfig.labels.headerSubtitle ?? "")}
        showLoading={loading}
        previousStepBackXTimes={1}
      />
      <Input
        id="card__input"
        labelText={t(flowConfig.labels.input.text ?? "")}
        isFilled={labelCardVisible}
        isError={!!(errors.cardNumber || errors.blocked || errors.submitError)}
        errorMsg={
          errors.cardNumber?.message ||
          errors.blocked?.message ||
          errors.submitError?.message ||
          ""
        }
        errorMsgBlocked={errors.blocked ? errors.blocked.message : ""}
        onChange={handleCardChange}
        onKeyPress={handleKeyPress}
        value={cardValue}
        ariaLabel={t(flowConfig.labels.input.aria ?? "")}
      />
      {errors.blocked && isAdminBlocked && (
        <div className="h-10 text-7xl font-bold text-center mb-30">
          <span
            onClick={handleContactFormLinkClick}
            className="text-black hover:underline cursor-pointer font-bold"
          >
            {" "}
            {t("common.contact_form")}{" "}
          </span>
        </div>
      )}
      <Button
        disabled={loading}>
        {t(flowConfig.labels.nextButton)}
      </Button>
      {flowConfig.showFreshStart && phoneFreshStartEligible && (
        <>
          <Divider title={t("common.or")} />
          <FreshStartButton onClick={freshStart} />
        </>
      )}
      <NeedHelpButton />
      <CredentialsFooter
        contextualInformationVisible={false}
        footerAppsWebsitesIconsVisible={false}
      />
    </CredentialsForm>
  );
}

export const createValidatePasswordPage = (flowConfig: AuthFlowType) => {
  const config = FLOW_CONFIGS[flowConfig];
  return () => <RecoverPinPage flowConfig={config} />;
};

export const SharedRecoverPinPage = createValidatePasswordPage(AuthFlowType.RECOVER); 
